import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface RateComponent {
  readonly rateCardComponentId: string;
  readonly uomCode: string;
  readonly rateInrMinor: number;
}

interface RateCardDetail {
  readonly rateMasterId: string;
  readonly rateCode: string;
  readonly vendorName: string | null;
  readonly brandingTypeCode: string;
  readonly territoryCode: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
  readonly status: string;
  readonly components: readonly RateComponent[];
}

/** L17.03 — Rate card detail (`/admin/rate-master/cards/:id`). */
export function M17RateCardDetailPage(): ReactNode {
  const { day } = useBusinessDay();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const { money } = useBusinessNumber();
  const [card, setCard] = useState<RateCardDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: RateCardDetail }>(`/v1/m17/rates/${id}`, { token })
      .then((r) => { if (alive) setCard(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [id, token]);

  if (error) return <p role="alert">{error}</p>;
  if (!card) return <p>{t('m17.rateCardDetail.loading')}</p>;
  return (
    <main aria-label="rate-card-detail">
      <h1>{t('m17.rateCardDetail.title', { code: card.rateCode })}</h1>
      <dl>
        <dt>{t('m17.rateCardDetail.vendor')}</dt><dd>{card.vendorName ?? t('m17.rateCardDetail.generic')}</dd>
        <dt>{t('m17.rateCardDetail.branding')}</dt><dd>{card.brandingTypeCode}</dd>
        <dt>{t('m17.rateCardDetail.territory')}</dt><dd>{card.territoryCode}</dd>
        <dt>{t('m17.rateCardDetail.effectiveFrom')}</dt><dd>{day(card.effectiveFrom)}</dd>
        <dt>{t('m17.rateCardDetail.effectiveTo')}</dt><dd>{day(card.effectiveTo)}</dd>
        <dt>{t('m17.rateCardDetail.status')}</dt><dd>{card.status}</dd>
      </dl>
      <h2>{t('m17.rateCardDetail.components')}</h2>
      <table aria-label="rate-components-table">
        <thead><tr><th>{t('m17.rateCardDetail.colUom')}</th><th>{t('m17.rateCardDetail.colRate')}</th></tr></thead>
        <tbody>
          {card.components.map((c) => (
            <tr key={c.rateCardComponentId}>
              <td>{unitLabel(c.uomCode)}</td>
              <td>{money(c.rateInrMinor / 100)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
